/**
 * StudioGenome context — the app-tier seam that feeds the LIVE composed genome
 * into a lazy IDE/workspace chunk without that chunk importing the genome barrel
 * (#379-S2; the {@link useJumpRoutes} precedent for genome-derived data).
 *
 * The genome-graph RIGHT-dock panel projects the composed `studioGenome` into a
 * data-flow DAG (`buildGenomeGraph`). It used to read that genome via a
 * MODULE-SCOPE `import { studioGenome } from '../genome/genome.js'`, which closed
 * a latent circular dependency: `genome-graph-plugin` → `genome-graph-panel` →
 * `genome/genome` (which composes `genomeGraphPanelPlugin`) → `genome-graph-plugin`.
 * The panel now reads the genome from THIS context instead — fed DOWN from
 * `App.tsx`, which already holds the live genome (`main.tsx` hoists it into state
 * and passes it as a prop; it's a stable ref that changes only on a restage). No
 * panel module-scope genome import → the cycle is gone.
 *
 * The default is `null` (a stable value → selector-loop safe): a bare panel
 * render with no provider simply renders a degraded "genome unavailable" state
 * rather than throwing, so the panel stays mountable in isolation.
 *
 * @module @ubiclouds/v5-studio/app/studio-genome-context
 */

import { createContext, useContext, type ReactNode } from 'react';
import type { GenomeV3 } from '@ubiclouds/v5-sdk/genome';

const StudioGenomeContext = createContext<GenomeV3 | null>(null);

export function StudioGenomeProvider({
  value,
  children,
}: {
  readonly value: GenomeV3 | null;
  readonly children: ReactNode;
}): ReactNode {
  return <StudioGenomeContext.Provider value={value}>{children}</StudioGenomeContext.Provider>;
}

/** The live composed genome (`null` outside a provider). A stable ref. */
export function useStudioGenome(): GenomeV3 | null {
  return useContext(StudioGenomeContext);
}
